import React, { useEffect } from 'react';
import { isEmpty } from 'lodash';
import { useHistory, useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core';

import Hidden from '@material-ui/core/Hidden';
import Dialog from '@material-ui/core/Dialog';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Slide from '@material-ui/core/Slide';

import helper from '@/helper';
import config from '@/configs/config';
import firebase from '@/services/firebase';
import useFirebaseDatabaseQuery from '@/hooks/useFirebaseDatabaseQuery';
import { useShoppingCartContext } from '@/contexts/useShoppingCartContext';

import CountTextField from '@/components/CountTextField';
import DeliveryMethodInput, { isDeliveryMethodDataComplete } from '../../../../../components/DeliveryMethodInput';

const useStyles = makeStyles((theme) => ({
  dialogScrollPaper: { alignItems: 'flex-end' },
  dialogPaper: {
    height: 'auto',
    backgroundColor: theme.palette.grey[300],
    paddingBottom: 'env(safe-area-inset-bottom, 0)',
  },
}));

const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />); // eslint-disable-line

const CreateOrder = ({
  isOpen = true,
  handleClose: triggerClose,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const { preOrderId } = useParams();
  const [buyCount, setBuyCount] = React.useState(1);

  const dataRef = firebase.database().ref(`/${process.env.REACT_APP_MODE}/pre-order/preOrderEvents/${preOrderId}`);
  const { data: preOrder } = useFirebaseDatabaseQuery(dataRef);

  const preOrderPrice = helper.getBasePrice(preOrder);

  const total = buyCount * preOrderPrice;

  const [deliveryMethod, setDeliveryMethod] = React.useState({});
  const isDeliveryMethodComplete = React.useMemo(
    () => isDeliveryMethodDataComplete(deliveryMethod, preOrder),
    [deliveryMethod, preOrder]
  );

  const {
    shoppingCart,
    addPreOrderToShoppingCart,
  } = useShoppingCartContext();

  useEffect(() => {
    if (!isEmpty(preOrder)) {
      const { minimumPurchaseAmount } = preOrder || {};
      setBuyCount(minimumPurchaseAmount || 1);
    }
  }, [preOrder]);

  const handleCheckout = React.useCallback(() => {
    addPreOrderToShoppingCart({
      preOrderId,
      amount: buyCount,
      ...(deliveryMethod || {}),
    });

    history.push('/pre-orders/checkout', {
      [preOrderId]: {
        ...deliveryMethod,
        ...preOrder,
        amount: buyCount,
      },
    });
  }, [history, preOrder, deliveryMethod, buyCount, preOrderId, addPreOrderToShoppingCart]);

  const content = (
    <Box py={1}>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Grid container alignItems="center">
            <Grid item xs={3}>
              <Box fontSize="body1.fontSize" fontWeight="fontWeightBold">訂購數量</Box>
            </Grid>
            <Grid item sm={5} xs={9}>
              <Box>
                <CountTextField
                  defaultValue={buyCount}
                  value={buyCount}
                  minValue={preOrder?.minimumPurchaseAmount || 1}
                  onChange={setBuyCount}
                />
              </Box>
            </Grid>
            <Hidden only="xs">
              <Grid item sm={4}>
                <Box ml={2} borderRadius={20} overflow="hidden">
                  <Button
                    style={{ opacity: shoppingCart[preOrderId] ? 0.5 : 1 }}
                    height={30}
                    variant="contained"
                    fullWidth
                    color="primary"
                    onClick={() => {
                      if (!shoppingCart[preOrderId]) {
                        addPreOrderToShoppingCart({
                          preOrderId,
                          amount: buyCount,
                          ...(deliveryMethod || {}),
                        });
                      }
                    }}
                  >
                    加入購物車
                  </Button>
                </Box>
              </Grid>
            </Hidden>
          </Grid>
        </Grid>
        <Grid item container xs={12}>
          <Grid container justify="space-between" wrap="nowrap">
            <Grid item xs={3}>
              <Box fontSize="body1.fontSize" fontWeight="fontWeightBold">總計金額</Box>
            </Grid>
            <Grid item xs={9}>
              <Box fontSize="body1.fontSize" fontWeight="fontWeightBold">
                {`${total}元`}
              </Box>
            </Grid>
          </Grid>
        </Grid>
        <Grid item container xs={12}>
          <Grid item container xs={12} justify="space-between">
            <Grid item xs={3}>
              <Box fontSize="body1.fontSize" fontWeight="fontWeightBold">取貨地點</Box>
            </Grid>
            <Grid item xs={9}>
              <DeliveryMethodInput
                preOrder={preOrder}
                onChange={setDeliveryMethod}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <>
      <Hidden only="xs">
        <Box my={1} px={1} style={{ backgroundColor: 'rgb(242, 242, 242)' }}>
          {content}
        </Box>
        <Grid container alignItems="center">
          <Grid item sm>
            <Button
              variant="contained"
              fullWidth
              color="primary"
              onClick={handleCheckout}
              disabled={!isDeliveryMethodComplete}
            >
              <Box py={1} fontSize="body1.fontSize" fontWeight="fontWeightBold">
                預購結帳
              </Box>
            </Button>
          </Grid>
        </Grid>
      </Hidden>
      <Hidden only="sm">
        <Dialog
          classes={{
            scrollPaper: classes.dialogScrollPaper,
            paper: classes.dialogPaper,
          }}
          fullScreen
          open={isOpen}
          onClose={triggerClose}
          TransitionComponent={Transition}
        >
          <Box px={3}>
            <Box py={1}>
              <Grid item xs={12}>
                <Grid container wrap="nowrap">
                  <Grid item>
                    <Box position="relative" width={130} />
                    <Box
                      position="absolute"
                      top={-65}
                      width={130}
                      height={130}
                      borderRadius={12}
                      p={0.5}
                      bgcolor="common.white"
                      overflow="hidden"
                    >
                      <Box
                        width={1}
                        height={1}
                        overflow="hidden"
                        borderRadius={10}
                      >
                        <img
                          style={{ objectFit: 'cover' }}
                          width="100%"
                          height="100%"
                          src={`${config.RESOURCE_URI}${preOrder?.coverImage}`}
                          alt=""
                        />
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item>
                    <Box px={2}>
                      <Box fontSize="h6.fontSize" fontWeight="fontWeightBold">{preOrder?.title}</Box>
                      <Box fontSize="body1.fontSize" fontWeight="fontWeightMedium">{preOrder?.slogan}</Box>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
            {content}
          </Box>
          <Box width={1}>
            <Button
              variant="contained"
              fullWidth
              size="large"
              color="secondary"
              onClick={handleCheckout}
              disabled={!isDeliveryMethodComplete}
            >
              <Box py={1} fontSize="body1.fontSize">
                前往結帳
              </Box>
            </Button>
          </Box>
        </Dialog>
      </Hidden>
    </>
  );
};

export default CreateOrder;
